'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/context/LanguageContext';
import { getTranslations } from '@/lib/i18n';

const donationAmounts = [500, 1000, 2500, 5000, 10000];

const impactMapEn: Record<number, string> = {
  500: "Funds a child's books and stationery for one year",
  1000: "Covers one month of after-school tutoring for 2 children",
  2500: "Sponsors a health camp for 25 community members",
  5000: "Provides a partial scholarship for one semester",
  10000: "Funds teacher training for one educator",
};

const impactMapKn: Record<number, string> = {
  500: "ಒಂದು ವರ್ಷಕ್ಕೆ ಮಗುವಿನ ಪುಸ್ತಕ ಮತ್ತು ಲೇಖನ ಸಾಮಗ್ರಿ ನಿಧಿ",
  1000: "2 ಮಕ್ಕಳಿಗೆ ಒಂದು ತಿಂಗಳ ಶಾಲೆಯ ನಂತರದ ಟ್ಯೂಟರಿಂಗ್",
  2500: "25 ಸಮುದಾಯ ಸದಸ್ಯರಿಗೆ ಆರೋಗ್ಯ ಶಿಬಿರ ಪ್ರಾಯೋಜಕತ್ವ",
  5000: "ಒಂದು ಸೆಮಿಸ್ಟರ್‌ಗೆ ಭಾಗಶಃ ವಿದ್ಯಾರ್ಥಿವೇತನ",
  10000: "ಒಬ್ಬ ಶಿಕ್ಷಕರಿಗೆ ಶಿಕ್ಷಕ ತರಬೇತಿ ನಿಧಿ",
};

interface FormData {
  firstName: string; lastName: string; email: string; phone: string; pan: string;
  amount: string; customAmount: string; frequency: 'one-time' | 'monthly' | 'yearly';
  program: string; message: string; receiptRequired: boolean;
}

export default function DonatePage() {
  const { language } = useLanguage();
  const t = getTranslations(language);
  const kn = language === 'kn';

  const [selectedAmount, setSelectedAmount] = useState<number | null>(2500);
  const [form, setForm] = useState<FormData>({ firstName: '', lastName: '', email: '', phone: '', pan: '', amount: '2500', customAmount: '', frequency: 'one-time', program: 'general', message: '', receiptRequired: true });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Partial<FormData>>({});

  const handleAmountSelect = (amt: number) => { setSelectedAmount(amt); setForm((f) => ({ ...f, amount: String(amt), customAmount: '' })); };
  const handleCustomAmount = (val: string) => { setSelectedAmount(null); setForm((f) => ({ ...f, customAmount: val, amount: val })); };

  const validate = () => {
    const e: Partial<FormData> = {};
    if (!form.firstName.trim()) e.firstName = kn ? 'ಮೊದಲ ಹೆಸರು ಅಗತ್ಯ' : 'First name is required';
    if (!form.lastName.trim()) e.lastName = kn ? 'ಕೊನೆಯ ಹೆಸರು ಅಗತ್ಯ' : 'Last name is required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = kn ? 'ಮಾನ್ಯ ಇಮೇಲ್ ಅಗತ್ಯ' : 'Valid email is required';
    if (!form.phone.trim() || !/^[6-9]\d{9}$/.test(form.phone)) e.phone = kn ? 'ಮಾನ್ಯ 10-ಅಂಕಿ ಮೊಬೈಲ್ ಸಂಖ್ಯೆ ಅಗತ್ಯ' : 'Valid 10-digit mobile number required';
    if (!form.amount || Number(form.amount) < 100) e.amount = kn ? 'ಕನಿಷ್ಠ ದೇಣಿಗೆ ₹100' : 'Minimum donation is ₹100';
    return e;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSubmitted(true);
  };

  const currentAmount = Number(form.customAmount || form.amount);
  const impactMap = kn ? impactMapKn : impactMapEn;

  return (
    <div className="bg-white min-h-screen">
      <Header />
      <main className="pt-24">
        <section className="hero-gradient py-16 px-6 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-5xl mb-4">🙏</div>
            <h1 className={`text-4xl sm:text-5xl font-bold mb-3 ${kn ? 'font-kannada' : ''}`}>
              {t.donate.heroTitle} <span className="text-saffron-light">{t.donate.heroHighlight}</span>
            </h1>
            <p className={`text-white/75 max-w-xl mx-auto ${kn ? 'font-kannada' : ''}`}>{t.donate.heroSubtitle}</p>
          </div>
        </section>

        <div className="bg-indigo py-4 px-6">
          <div className="max-w-5xl mx-auto flex flex-wrap justify-center gap-6 text-white/70 text-sm">
            {[kn ? '🔒 ಸುರಕ್ಷಿತ ದೇಣಿಗೆ' : '🔒 Secure Donation', kn ? '✅ 80G ತೆರಿಗೆ ವಿನಾಯಿತಿ' : '✅ 80G Tax Exempt', kn ? '📋 ತಕ್ಷಣ ರಸೀದಿ' : '📋 Instant Receipt', kn ? '🏛️ ನೋಂದಾಯಿತ ಟ್ರಸ್ಟ್' : '🏛️ Registered Trust'].map((b) => (
              <span key={b} className={`font-medium ${kn ? 'font-kannada' : ''}`}>{b}</span>
            ))}
          </div>
        </div>

        {submitted ? (
          <section className="py-20 px-6">
            <div className="max-w-lg mx-auto text-center">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
              </div>
              <h2 className={`text-3xl font-bold text-indigo mb-3 ${kn ? 'font-kannada' : ''}`}>{t.donate.successTitle}</h2>
              <p className={`text-neutral-600 mb-6 ${kn ? 'font-kannada' : ''}`}>{t.donate.successMsg}</p>
              <div className="bg-saffron-pale rounded-2xl p-6 mb-8 text-left">
                <h4 className={`font-bold text-indigo mb-3 ${kn ? 'font-kannada' : ''}`}>{kn ? 'ದೇಣಿಗೆ ಸಾರಾಂಶ' : 'Donation Summary'}</h4>
                <div className="space-y-2 text-sm text-neutral-700">
                  <div className="flex justify-between"><span className={kn ? 'font-kannada' : ''}>{kn ? 'ದಾನಿ ಹೆಸರು' : 'Donor Name'}</span><span className="font-medium">{form.firstName} {form.lastName}</span></div>
                  <div className="flex justify-between"><span className={kn ? 'font-kannada' : ''}>{kn ? 'ಮೊತ್ತ' : 'Amount'}</span><span className="font-bold text-saffron">₹{Number(form.amount).toLocaleString('en-IN')}</span></div>
                </div>
              </div>
              <Link href="/home" className={`px-8 py-3 btn-saffron rounded-full font-semibold ${kn ? 'font-kannada' : ''}`}>{kn ? 'ಮುಖಪುಟಕ್ಕೆ ಹಿಂತಿರುಗಿ' : 'Back to Home'}</Link>
            </div>
          </section>
        ) : (
          <section className="py-16 px-6">
            <div className="max-w-5xl mx-auto grid lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2">
                <form onSubmit={handleSubmit} className="space-y-8">
                  <div>
                    <h3 className={`text-lg font-bold text-indigo mb-4 ${kn ? 'font-kannada' : ''}`}>{t.donate.frequency}</h3>
                    <div className="flex gap-3 flex-wrap">
                      {(['one-time', 'monthly', 'yearly'] as const).map((freq) => (
                        <button key={freq} type="button" onClick={() => setForm((f) => ({ ...f, frequency: freq }))}
                          className={`px-5 py-2.5 rounded-full text-sm font-semibold border-2 transition-all ${kn ? 'font-kannada' : ''} ${form.frequency === freq ? 'bg-saffron border-saffron text-white' : 'border-neutral-200 text-neutral-600 hover:border-saffron hover:text-saffron'}`}>
                          {freq === 'one-time' ? t.donate.oneTime : freq === 'monthly' ? t.donate.monthly : t.donate.yearly}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className={`text-lg font-bold text-indigo mb-4 ${kn ? 'font-kannada' : ''}`}>{t.donate.chooseAmount}</h3>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-4">
                      {donationAmounts.map((amt) => (
                        <button key={amt} type="button" onClick={() => handleAmountSelect(amt)}
                          className={`py-3 rounded-xl text-sm font-bold border-2 transition-all ${selectedAmount === amt ? 'bg-saffron border-saffron text-white' : 'border-neutral-200 text-neutral-700 hover:border-saffron hover:text-saffron'}`}>
                          ₹{amt.toLocaleString('en-IN')}
                        </button>
                      ))}
                    </div>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500 font-semibold">₹</span>
                      <input type="number" placeholder={kn ? 'ಕಸ್ಟಮ್ ಮೊತ್ತ ನಮೂದಿಸಿ (ಕನಿಷ್ಠ ₹100)' : 'Enter custom amount (min ₹100)'} value={form.customAmount}
                        onChange={(e) => handleCustomAmount(e.target.value)}
                        className="w-full pl-8 pr-4 py-3 border-2 border-neutral-200 rounded-xl text-sm focus:outline-none focus:border-saffron transition-colors" min="100" />
                    </div>
                    {errors.amount && <p className={`text-crimson text-xs mt-1 ${kn ? 'font-kannada' : ''}`}>{errors.amount}</p>}
                    {currentAmount >= 100 && impactMap[currentAmount] && (
                      <div className={`mt-3 p-3 bg-saffron-pale rounded-xl text-sm text-saffron font-medium ${kn ? 'font-kannada' : ''}`}>💡 {impactMap[currentAmount]}</div>
                    )}
                  </div>

                  <div>
                    <h3 className={`text-lg font-bold text-indigo mb-4 ${kn ? 'font-kannada' : ''}`}>{t.donate.program}</h3>
                    <select value={form.program} onChange={(e) => setForm((f) => ({ ...f, program: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-neutral-200 rounded-xl text-sm focus:outline-none focus:border-saffron transition-colors bg-white">
                      <option value="general">{kn ? 'ಸಾಮಾನ್ಯ ನಿಧಿ (ಅತ್ಯಂತ ಅಗತ್ಯವಿರುವ ಕಡೆ)' : 'General Fund (Where Most Needed)'}</option>
                      <option value="education">{kn ? 'ಶಿಕ್ಷಣ ಕಾರ್ಯಕ್ರಮ' : 'Education Program'}</option>
                      <option value="scholarships">{kn ? 'ವಿದ್ಯಾರ್ಥಿವೇತನ' : 'Scholarships'}</option>
                      <option value="teacher-training">{kn ? 'ಶಿಕ್ಷಕ ತರಬೇತಿ' : 'Teacher Training'}</option>
                      <option value="community-health">{kn ? 'ಸಮುದಾಯ ಆರೋಗ್ಯ' : 'Community Health'}</option>
                      <option value="women-empowerment">{kn ? 'ಮಹಿಳಾ ಮತ್ತು ಯುವ ಸಬಲೀಕರಣ' : 'Women & Youth Empowerment'}</option>
                    </select>
                  </div>

                  <div>
                    <h3 className={`text-lg font-bold text-indigo mb-4 ${kn ? 'font-kannada' : ''}`}>{t.donate.personalDetails}</h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className={`block text-sm font-medium text-neutral-700 mb-1.5 ${kn ? 'font-kannada' : ''}`}>{t.donate.firstName} *</label>
                        <input type="text" value={form.firstName} onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))} placeholder="Ramesh"
                          className={`w-full px-4 py-3 border-2 rounded-xl text-sm focus:outline-none transition-colors ${errors.firstName ? 'border-crimson' : 'border-neutral-200 focus:border-saffron'}`} />
                        {errors.firstName && <p className={`text-crimson text-xs mt-1 ${kn ? 'font-kannada' : ''}`}>{errors.firstName}</p>}
                      </div>
                      <div>
                        <label className={`block text-sm font-medium text-neutral-700 mb-1.5 ${kn ? 'font-kannada' : ''}`}>{t.donate.lastName} *</label>
                        <input type="text" value={form.lastName} onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))} placeholder="Kumar"
                          className={`w-full px-4 py-3 border-2 rounded-xl text-sm focus:outline-none transition-colors ${errors.lastName ? 'border-crimson' : 'border-neutral-200 focus:border-saffron'}`} />
                        {errors.lastName && <p className={`text-crimson text-xs mt-1 ${kn ? 'font-kannada' : ''}`}>{errors.lastName}</p>}
                      </div>
                      <div>
                        <label className={`block text-sm font-medium text-neutral-700 mb-1.5 ${kn ? 'font-kannada' : ''}`}>{t.donate.email} *</label>
                        <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="ramesh@example.com"
                          className={`w-full px-4 py-3 border-2 rounded-xl text-sm focus:outline-none transition-colors ${errors.email ? 'border-crimson' : 'border-neutral-200 focus:border-saffron'}`} />
                        {errors.email && <p className={`text-crimson text-xs mt-1 ${kn ? 'font-kannada' : ''}`}>{errors.email}</p>}
                      </div>
                      <div>
                        <label className={`block text-sm font-medium text-neutral-700 mb-1.5 ${kn ? 'font-kannada' : ''}`}>{t.donate.phone} *</label>
                        <input type="tel" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="9876543210" maxLength={10}
                          className={`w-full px-4 py-3 border-2 rounded-xl text-sm focus:outline-none transition-colors ${errors.phone ? 'border-crimson' : 'border-neutral-200 focus:border-saffron'}`} />
                        {errors.phone && <p className={`text-crimson text-xs mt-1 ${kn ? 'font-kannada' : ''}`}>{errors.phone}</p>}
                      </div>
                      <div className="sm:col-span-2">
                        <label className={`block text-sm font-medium text-neutral-700 mb-1.5 ${kn ? 'font-kannada' : ''}`}>{t.donate.pan} <span className="text-neutral-400 font-normal">({t.donate.panOptional})</span></label>
                        <input type="text" value={form.pan} onChange={(e) => setForm((f) => ({ ...f, pan: e.target.value.toUpperCase() }))} placeholder="ABCDE1234F" maxLength={10}
                          className="w-full px-4 py-3 border-2 border-neutral-200 rounded-xl text-sm focus:outline-none focus:border-saffron transition-colors font-mono" />
                      </div>
                      <div className="sm:col-span-2">
                        <label className={`block text-sm font-medium text-neutral-700 mb-1.5 ${kn ? 'font-kannada' : ''}`}>{t.donate.message}</label>
                        <textarea value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} placeholder={t.donate.messagePlaceholder}
                          rows={3} className="w-full px-4 py-3 border-2 border-neutral-200 rounded-xl text-sm focus:outline-none focus:border-saffron transition-colors resize-none" />
                      </div>
                    </div>
                    <label className="flex items-start gap-3 mt-4 cursor-pointer">
                      <input type="checkbox" checked={form.receiptRequired} onChange={(e) => setForm((f) => ({ ...f, receiptRequired: e.target.checked }))} className="mt-0.5 w-4 h-4 accent-saffron" />
                      <span className={`text-sm text-neutral-600 ${kn ? 'font-kannada' : ''}`}>{t.donate.receiptRequired}</span>
                    </label>
                  </div>

                  <button type="submit" className={`w-full py-4 btn-saffron rounded-xl font-bold text-base text-white ${kn ? 'font-kannada' : ''}`}>
                    {t.donate.submitBtn} — ₹{currentAmount >= 100 ? Number(form.amount).toLocaleString('en-IN') : '—'}
                  </button>
                </form>
              </div>

              <div className="space-y-6">
                <div className="bg-saffron-pale rounded-2xl p-6 border border-saffron/10">
                  <h4 className={`font-bold text-indigo mb-4 ${kn ? 'font-kannada' : ''}`}>{kn ? 'ಅರಿವುಗೆ ಏಕೆ ದೇಣಿಗೆ ನೀಡಬೇಕು?' : 'Why Donate to Arivu?'}</h4>
                  <ul className="space-y-3 text-sm text-neutral-700">
                    {(kn ? ['80G ಪ್ರಮಾಣಿತ — ತೆರಿಗೆಯಲ್ಲಿ 50% ಉಳಿಸಿ', '100% ಪಾರದರ್ಶಕ ನಿಧಿ ಬಳಕೆ', 'ಇಮೇಲ್ ಮೂಲಕ ತಕ್ಷಣ ದೇಣಿಗೆ ರಸೀದಿ', 'ದಾನಿಗಳಿಗೆ ವಾರ್ಷಿಕ ಪ್ರಭಾವ ವರದಿ', 'ನೇರ ಕಾರ್ಯಕ್ರಮ ಪ್ರಭಾವ ಟ್ರ್ಯಾಕಿಂಗ್'] : ['80G certified — save up to 50% in taxes', '100% transparent fund utilization', 'Instant donation receipt via email', 'Annual impact report to donors', 'Direct program impact tracking']).map((item) => (
                      <li key={item} className={`flex gap-2 ${kn ? 'font-kannada' : ''}`}>
                        <svg className="w-4 h-4 text-saffron flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-indigo-pale rounded-2xl p-6 border border-indigo/10">
                  <h4 className={`font-bold text-indigo mb-4 ${kn ? 'font-kannada' : ''}`}>{t.donate.bankTransfer}</h4>
                  <div className="space-y-2 text-sm text-neutral-700">
                    <div><span className="text-neutral-500">{kn ? 'ಖಾತೆ ಹೆಸರು:' : 'Account Name:'}</span><br /><strong>Arivu Foundation</strong></div>
                    <div><span className="text-neutral-500">{kn ? 'ಖಾತೆ ಸಂಖ್ಯೆ:' : 'Account No:'}</span><br /><strong className="font-mono">BANK_ACCOUNT_NUMBER</strong></div>
                    <div><span className="text-neutral-500">IFSC:</span><br /><strong className="font-mono">BANK_ACCOUNT_IFSC</strong></div>
                  </div>
                </div>

                <div className="bg-neutral-50 rounded-2xl p-6 border border-neutral-200 text-center">
                  <h4 className={`font-bold text-indigo mb-3 ${kn ? 'font-kannada' : ''}`}>{t.donate.upiTitle}</h4>
                  <div className="w-32 h-32 bg-neutral-200 rounded-xl mx-auto mb-3 flex items-center justify-center text-neutral-400 text-xs">QR Code<br />{kn ? 'ಶೀಘ್ರದಲ್ಲಿ' : 'Coming Soon'}</div>
                  <p className="text-sm text-neutral-600 font-mono">UPI_ID@bank</p>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
